/**
 * Client for `POST /api/intents/push-to-agent` (B8 / 14ym9bv75mx): the Messenger
 * hands a lead to the Agent as ONE server transaction. The server checks the
 * phone number, saves it and the typed name on the customer, and marks the
 * sequence record pushed with the summary and callback pin. Same fetch / auth /
 * clock shape as `@/api/overrideIntents`: never throws; a failure carries
 * `kind`. A replay on a pushed lead is a 200 `alreadyResolved` no-op.
 */
import { authHeaders, isSigningIn, whenSignedIn } from '@/api/persist';
import { intentErrorKind, type IntentFailure } from '@/api/intents';
import { useSimulationStore } from '@/state/simulationStore';

export interface PushToAgentPayload {
  customerId: string;
  phone: string;
  firstName?: string;
  lastName?: string;
  summary?: string;
  pinnedCallbackAt?: string;
  /** tv-9nae: the thread's latest entry time as this tab sees it (the server's push marker lands after it). */
  threadLastAt?: string;
  nowIso?: string;
}

export type PushToAgentResult = { ok: true; alreadyResolved: boolean } | IntentFailure;

export async function pushToAgentIntent(payload: PushToAgentPayload): Promise<PushToAgentResult> {
  if (isSigningIn()) await whenSignedIn();
  let res: Response;
  try {
    res = await fetch('/api/intents/push-to-agent', {
      method: 'POST',
      headers: { 'content-type': 'application/json', ...authHeaders() },
      body: JSON.stringify({ ...payload, nowIso: payload.nowIso ?? useSimulationStore.getState().clockIso }),
    });
  } catch {
    return { ok: false, kind: 'transient', status: 0, error: 'network' };
  }
  const body = (await res.json().catch(() => undefined)) as
    | { alreadyResolved?: unknown; error?: string; code?: string; message?: string }
    | undefined;
  if (res.ok) return { ok: true, alreadyResolved: body?.alreadyResolved === true };
  return {
    ok: false,
    kind: intentErrorKind(res.status, body),
    status: res.status,
    error: body?.error ?? `http-${res.status}`,
    ...(typeof body?.code === 'string' ? { code: body.code } : {}),
    ...(body?.message ? { message: body.message } : {}),
  };
}
